import _ from "underscore";
import { CanvasHandler } from "../../handlers";
import { FabricObject } from "../../types/FabricCustomType";
import { IObjectSelector } from "../../types/HandlerObjectType";
import { getSvgObject, updateObjectStatusInDb, createObjectModel} from "../../utils/ObjectUtil";
import { TooltipSeatSelector } from "../../components/tooltip/TooltipSeatSelector";
import store from "../../redux/store";
import {
  addSeatToSelection,
  removeSeatToSelection,
} from "../../redux/designerPropertiesSlice";
import { isTouchDevice, isTouchInteraction } from "../../utils/InteractionUtil";
import { IconKey, Icons, ObjectTypesEnum, SeatIcons } from "../../enums";
import { fabric } from 'fabric';

interface SeatCallback {
  value: number;
  status: string;
  categoryKey: string;
  category: string;
  selectorKey: string;
  table: string;
  section: string;
  objectType: string;
  seatList: any[];
}
class Table implements IObjectSelector {
  private canvasHandler: CanvasHandler;
  private targetSeat!: FabricObject<Object> | null;

  constructor(canvasHandler: CanvasHandler) {
    this.canvasHandler = canvasHandler;
  }

  public activateObjectHover(event: any, table: FabricObject<Object>): any {
    if (this.targetSeat != table) {
      this.deactivateObjectHover();//Elimina el hover del objeto anterior
      const tableShape = table.item(0);
      this.setTooltip(event, table);

      if (table.status == "Free" && table.available) {
        const {
          properties: { roundTableRadius, rectTableDimensions },
        } = store.getState();
        tableShape.set({
          fill: `${tableShape.stroke}90`,
        });
        switch (tableShape.type) {
          case ObjectTypesEnum.Circle:
            tableShape.set({
              radius: roundTableRadius + 2.5,
            });
          break;
          case ObjectTypesEnum.Rect:
            tableShape.set({
              height: rectTableDimensions.height + 5
            });
          break;
        }
      }
      this.targetSeat = table;
      this.canvasHandler.canvas.requestRenderAll();
    }

    if (this.targetSeat && this.targetSeat.available && this.targetSeat.status == "Free")
      this.canvasHandler.canvas.setCursor("pointer");
    else
      this.canvasHandler.canvas.setCursor("default");
  }

  public async handleSelectObject(tableKey: string, table: FabricObject<Object>) {
    await this.changeStatus(tableKey, table);
  }

  public async handleObjectLockUpdate(object: any, hubItem: any, selectorKey: any): Promise<any> {
    const {
      properties: { channels },
    } = store.getState();
    if (object) {
      if (hubItem.status == "Free" && ((hubItem.channel?.key ?? null) == null || channels.includes(hubItem.channel?.key ?? null))) {
        this.releaseInCanvas(object);
      } else {
        this.holdInCanvas(object, (hubItem.channel?.key ?? null) == null ? hubItem.status : "Reserved", selectorKey);
      }

      if (window.redseats.responseCallback && !isTouchDevice())
        window.redseats.responseCallback();
      else
        this.deactivateObjectHover();
    }
  }

  public deactivateObjectHover = () => {
    if (this.targetSeat) {
      this.canvasHandler.tooltipHandler.hide();
      this.canvasHandler.canvas.setCursor("default");
      if (this.targetSeat.status == "Free" && this.targetSeat.available) {
        const tableShape = this.targetSeat.item(0);
        const {
          properties: { roundTableRadius, rectTableDimensions },
        } = store.getState();
        tableShape.set({
          fill: `${tableShape.stroke}40`
        });
        switch (tableShape.type) {
          case ObjectTypesEnum.Circle:
            tableShape.set({
              radius: roundTableRadius,
            });
          break;
          case ObjectTypesEnum.Rect:
            tableShape.set({
              height: rectTableDimensions.height
            });
          break;
        }
      }
      this.canvasHandler.canvas.requestRenderAll();
      this.targetSeat = null;
    }
    else {
      this.canvasHandler.tooltipHandler.hide();
      this.canvasHandler.canvas.setCursor("default");
    }
  };

  private async changeStatus(tableKey: string, table: FabricObject<Object>): Promise<any> {
    const {
      properties: { selection },
    } = store.getState();
    this.canvasHandler.eventHandler.panning = false;
    let seatsLength = 0;
    if (table.available) {
      let category = selection.find((select) => {
        return table.category.key === select.key
      });
      seatsLength = category.seats.length;
    }
    if (
      table.available  ||
      (table.status == "Reserved" && table.isHoldByMe)
    ) {
      let isSuccessful = false;
      let isSelection = false;
      const objectModel = createObjectModel(tableKey, table);
      if (table.status == "Free") {
        isSelection = true;
        isSuccessful = await updateObjectStatusInDb([objectModel], "Reserved", true);
      } else if (table.status == "Reserved" && table.isHoldByMe) {
        table.set({
          status: "Reserved",
          isHoldByMe: true,
        });
        isSuccessful = await updateObjectStatusInDb([objectModel], "Free", true);
      }

      const seatCallback = this.createSeatCallback(table);

      if (isSuccessful) {
        if (isSelection) window.redseats.onSeatSelected(seatCallback);
        else window.redseats.onSeatDeselected(seatCallback);
      } else window.redseats.onFailedSeatSelection(seatCallback);
    }
  }

  private async holdInCanvas(table: any, status: string, selectorKey: string) {
    const canvas = this.canvasHandler.canvas;
    const selector = JSON.parse(sessionStorage.getItem("rs_selector"));
    const isHoldByMe: boolean = selector.key == selectorKey;
    const {
      properties: { selection, roundTableRadius },
    } = store.getState();
    table.set({
      status: status,
      isHoldByMe: isHoldByMe,
    });
    const tableShape = table.item(0);
    const seatText = table.item(1);
    const colorFill = table.statusColor[status];

    const baseSize = Math.min(tableShape.width, tableShape.height);
    const iconSize = baseSize * 0.7;

    if (isHoldByMe) {
      let index = 0;
      selection.some((category, i) => {
        if (category.key === table.category.key) {
          index = i
        }
      });
      store.dispatch(addSeatToSelection({ index, seat: table.id }));
      tableShape.set({
        fill: table.statusColor.Free
      });
      seatText.set({
        visible: false,
      });

      const seatOldIcon = table.item(2);
      if (seatOldIcon)
        table.remove(seatOldIcon);
      
      const seatCheckIcon = getSvgObject(Icons.check, iconSize, '#ffffff');
      seatCheckIcon.set({
        angle: seatText.angle,
      });
      table.insertAt(seatCheckIcon, 2, true);

    } else {
      debugger
      tableShape.set({
        fill: colorFill,
        stroke: "transparent",
      });
      seatText.set({
        visible: false,
      });

      const seatOldIcon = table.item(2);
      if (seatOldIcon)
        table.remove(seatOldIcon);
      const statusIcon = table.statusIcon?.[status]?.toLowerCase() as IconKey;
      if (statusIcon && statusIcon in SeatIcons) {
        const icon = getSvgObject(
          SeatIcons[statusIcon](iconSize-3),
          iconSize,
          "#ffffff"
        );
        icon.set({
          angle: seatText.angle,
        });
        table.insertAt(icon, 2, false);
      } 
    }

    canvas.requestRenderAll();
  }

  private async releaseInCanvas(seat: any) {
    const {
      properties: { selection },
    } = store.getState();
    seat.set({
      status: "Free",
      isHoldByMe: false,
      available: true,
    });
    const seatCircle = seat.item(0);
    const seatText = seat.item(1);
    const hasSpecialIcon = (seat.accesible || seat.companion || seat.restrictedView);

    let index = 0;
    selection.some((category, i) => {
      if (category.key === seat.category.key) {
        index = i
      }
    });
    store.dispatch(removeSeatToSelection({ index, seat: seat.id }));
    seatCircle.set({
      fill: `${seat.statusColor.Free}${this.canvasHandler.isInteractionEnabled ? "40" : "80"}`,
      stroke: this.canvasHandler.isInteractionEnabled ? seat.statusColor.Free : "transparent",
    });
    seatText.set({
      visible: !hasSpecialIcon && this.canvasHandler.isInteractionEnabled,
    });

    const seatCheckIcon = seat.item(2);
    if (seatCheckIcon) {
      seat.remove(seatCheckIcon);
    }

    if (hasSpecialIcon && this.canvasHandler.isInteractionEnabled) {
      if (seat.restrictedView)
        seat.insertAt(getSvgObject(Icons.restricted, (seatCircle.radius * 1.7), '#000000'), 2, true);
      else if (seat.accesible)
        seat.insertAt(getSvgObject(Icons.accesible, (seatCircle.radius * 1.7), '#000000'), 2, true);
      else if (seat.companion)
        seat.insertAt(getSvgObject(Icons.companion, (seatCircle.radius * 1.7), '#000000'), 2, true);
    }

    this.canvasHandler.canvas.requestRenderAll();
  }

  private setTooltip(event: any, seat: any) {
    const show = _.partial(this.canvasHandler.tooltipHandler.show, event.e);

    window.redseats.responseCallback = _.partial(
      this.showTooltip,
      show,
      seat,
      this,
      event
    );

    const seatCallback = this.createSeatCallback(seat);
    window.redseats.getTooltipInfo(seatCallback);
  }

  private showTooltip(
    show: any,
    seat: any,
    rowInstance: any,
    event: any,
    tooltipInfo: any
  ) {
    let handleSelectObject;
    if (isTouchInteraction(event))
      handleSelectObject = () => rowInstance.handleSelectObject(event.target.id, seat);

    const handleClose = () => {
      rowInstance.deactivateObjectHover();
    }

    window.redseats.responseCallback = _.partial(
      window.redseats.responseCallback,
      tooltipInfo
    );

    const htmlElement = TooltipSeatSelector(seat, tooltipInfo, handleSelectObject, handleClose);
    show(htmlElement);
  }

  private createSeatCallback(seat: any): SeatCallback {
    const selector = JSON.parse(sessionStorage.getItem("rs_selector"));
    return {
      value: seat.value,
      status: seat.status,
      categoryKey: seat.category.key,
      category: seat.category.name,
      selectorKey: selector.key,
      table: seat.tableLabel,
      section: seat.sectionLabel,
      objectType: "Table",
      seatList: seat.seatList
    };
  }
}
export default Table;